/**
 * Checkbox atom — brand-styled native checkbox with optional tooltip and configurable size.
 *
 * Uses design tokens from index.css: purple accent, light-lila border, dark-invirtus-soft
 * when disabled. No business logic; suitable for selection toggles (e.g. table row select).
 *
 * Props align with HTML <input type="checkbox"> (MDN) plus app-specific tooltip and styling:
 * - Controlled: pass `checked` + `onChange`.
 * - Uncontrolled: pass `defaultChecked` and optionally `name`/`value` for form submission.
 * - Size: "sm" | "md" (default 16px) | "lg" for different contexts.
 * - Shape: "square" (default) | "circle" — circle requires appearance-none (see styles).
 * - Accessibility: provide `aria-label` when there is no visible label, or associate via `id` + <label htmlFor>.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox
 */

import React, { useRef, useEffect } from 'react';
import { Tooltip } from '../../tooltip';
import type { CheckboxProps } from './Checkbox.types';
import { getCheckboxClasses, getCheckboxGlyphStyle } from './Checkbox.styles';

export const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  onChange,
  disabled = false,
  indeterminate = false,
  tooltip,
  tooltipVariant = 'top',
  size = 'sm',
  shape = 'square',
  className = '',
  'aria-label': ariaLabel,
  value,
  name,
  id,
  defaultChecked,
  style,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const glyphStyle = getCheckboxGlyphStyle({ checked, indeterminate });

  const input = (
    <input
      ref={inputRef}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      value={value}
      name={name}
      id={id}
      defaultChecked={defaultChecked}
      aria-label={
        ariaLabel ?? (tooltip?.trim() ? undefined : 'Toggle selection')
      }
      className={getCheckboxClasses(size, disabled, className, shape)}
      aria-checked={indeterminate ? 'mixed' : undefined}
      style={{ ...glyphStyle, ...style }}
      {...rest}
    />
  );

  if (tooltip?.trim()) {
    return (
      <Tooltip content={tooltip} variant={tooltipVariant}>
        {input}
      </Tooltip>
    );
  }

  return input;
};
